import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {Equipment, EquipmentStatus} from "../../api/model/equipment";
import {HttpErrorResponse} from "@angular/common/http";
import {EMPTY, fromEvent, Observable} from "rxjs";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ApiError} from "../../api/model/common";

import {MatSnackBar} from "@angular/material/snack-bar";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {debounceTime} from "rxjs/operators";
import {PurchaseOrder, PurchaseOrderMoveRequest, PurchaseOrderStatus} from "../../api/model/purchase-order";
import {apiErrorToMessage} from "../../common/util";
import {PurchaseOrderService} from "../../api/purchase-order.service";

@Component({
  selector: 'app-purchase-order-move-dialog',
  templateUrl: './move-dialog.component.html',
  styleUrls: ['./move-dialog.component.sass'],
  encapsulation: ViewEncapsulation.None,
})
export class PurchaseOrderMoveDialogComponent implements OnInit, AfterViewInit {
  apiError: string | undefined;
  loading: boolean = true;
  statuses: string[] = Object.values(EquipmentStatus);
  filteredPurchaseOrders: PurchaseOrder[] = [];
  initialPurchaseOrders: PurchaseOrder[] = [];
  selectedPurchaseOrder: PurchaseOrder | undefined;
  lastFilterValue: string = "";
  @ViewChild("filter") filterField!: ElementRef;

  constructor(
    private purchaseOrderService: PurchaseOrderService,
    public dialogRef: MatDialogRef<PurchaseOrderMoveDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MoveDialogData,
    private _snackBar: MatSnackBar,
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit() {
    this.purchaseOrderService.list([PurchaseOrderStatus.DRAFT], undefined, false).subscribe({
      next: orders => {
        console.log("PurchaseOrderMoveDialogComponent orders loaded", orders);
        this.initialPurchaseOrders = orders;
      },
      error: error => {
        const msg = apiErrorToMessage(error);
        this._snackBar.open(`Unfortunate error occurred. ${msg}`, "Okay :(");
        console.error("PurchaseOrderMoveDialogComponent error", msg)
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  ngAfterViewInit() {
    const keyUpEvent = fromEvent(this.filterField.nativeElement, 'keyup')
    keyUpEvent.pipe(debounceTime(500)).subscribe(c => {
      this._filterPurchaseOrders((c as any)?.target?.value)
    });
  }

  @HostListener('window:keyup.esc') onKeyUp() {
    this.dialogRef.close();
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.moveToForm.dirty;
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  onPrimaryClick() {
    this.onSubmit()
  }

  onSubmit() {
    this.apiError = undefined;

    if (this.moveToForm.controls.purchaseOrderId.dirty && this.moveToForm.controls.purchaseOrderId.valid && this.selectedPurchaseOrder) {
      const request = {} as PurchaseOrderMoveRequest;
      request.equipmentIds = this.data.equipment.map(e => e.id)
      request.moveToPurchaseOrderId = this.selectedPurchaseOrder.id;
      this.purchaseOrderService.move(request)
        .subscribe({
          next: (po) => {
            console.log(`PurchaseOrderMoveDialogComponent equipment move success`, request, po);
            this.dialogRef.close(po);
          },
          error: error => {
            console.log(`PurchaseOrderMoveDialogComponent equipment move failed`, request, error);
            this.apiError = ((error as HttpErrorResponse).error as ApiError).message || (error as HttpErrorResponse).message;
            return EMPTY;
          }
        });
    }
  }

  purchaseOrderAutocompleteSelect($event: MatAutocompleteSelectedEvent) {
    this.selectedPurchaseOrder = $event.option.value;
    this.lastFilterValue = "" + this.selectedPurchaseOrder?.name
    this.moveToForm.controls.purchaseOrderId.setValue(this.lastFilterValue);
  }

  private purchaseOrderSelectionValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    console.log("projectSelectionValidator validation triggered", this.selectedPurchaseOrder);
    return !this.selectedPurchaseOrder ? {purchaseOrderAutocompleted: {value: control.value}} : null;
  };

  moveToForm = new FormGroup({
    purchaseOrderId: new FormControl('', {
      validators: [Validators.required, this.purchaseOrderSelectionValidator],
      updateOn: 'blur'
    }),
  });

  private _filterPurchaseOrders(filter: string | null | undefined) {
    if (!filter || filter.length == 0) {
      this.filteredPurchaseOrders = this.initialPurchaseOrders.slice();
      return;
    }
    if (this.lastFilterValue != filter) {
      this.selectedPurchaseOrder = undefined;
      const f = filter.toLowerCase();
      this.filteredPurchaseOrders = this.initialPurchaseOrders.filter(po =>
        po.name.toLowerCase().includes(f)
        || po.equipment.some(e => e.manufacturer.toLowerCase().includes(f))
      );
    }
  }
}

export interface MoveDialogData {
  from?: PurchaseOrder;
  equipment: Equipment[];
}
